import { useEffect, useMemo } from 'react';
import { StanCard, StanFilters } from '@/components';
import { useTagManagement } from '../../queries';
import { useRecipientsActions, useRecipientsFilters } from '../../recipients/hooks';
import { useRecipientsStore } from '../../recipients/stores';
import { useRecipients } from '../../recipients/queries';
import { getRecipientFilterConfigs, RECIPIENTS_EXPORT_PAGE_SIZE } from '../../recipients/constants';
import { RecipientActionsMenu, RecipientsTable } from '../../recipients/components';
import { ExportCsvModal } from '../../recipients/components/ExportCsvModal';
import { SendOptInEmailModal } from '../../recipients/components/SendOptInEmailModal';
import { SendOptInEmailSuccessModal } from '../../recipients/components/SendOptInEmailSuccessModal';
import { RecipientsTabSkeleton } from './RecipientsTabSkeleton';

export const RecipientsTab = ({ isUserLoading }: { isUserLoading?: boolean }) => {
    const pageSize = useRecipientsStore(state => state.pageSize);
    const transformedFilters = useRecipientsStore(state => state.transformedFilters);
    const reset = useRecipientsStore(state => state.reset);

    const { availableTags } = useTagManagement({
        mode: 'include',
        selectedTags: [],
        onTagsChange: () => {},
        productOptions: [],
    });

    const { filterValues, handleFilterChange, handleResetFilters } = useRecipientsFilters();

    const filters = useMemo(() => getRecipientFilterConfigs(availableTags), [availableTags]);

    const { data: recipientsData } = useRecipients({
        page: 1,
        pageSize: pageSize,
        ...(transformedFilters && { filter_array: transformedFilters }),
    });

    const {
        handleExportCSV,
        handleSendOptInEmails,
        exportModal,
        optInModal,
        successModal,
        fetchPageData,
        handleCSVDataFetched,
        emailsSent,
        handleOptInSuccess,
        handleSuccessModalClose,
    } = useRecipientsActions();

    const tableTotalPages = recipientsData?.total_pages || 0;
    const totalExportPages = Math.ceil((tableTotalPages * pageSize) / RECIPIENTS_EXPORT_PAGE_SIZE);

    useEffect(() => {
        return () => reset();
    }, [reset]);

    if (isUserLoading) return <RecipientsTabSkeleton />;

    return (
        <>
            <StanCard className='relative'>
                <div className='absolute top-4 right-4'>
                    <RecipientActionsMenu onExportCSV={handleExportCSV} onSendOptInEmails={handleSendOptInEmails} />
                </div>

                <h3 className='mb-4'>Recipients</h3>

                <div className='mb-4'>
                    <p className='text-secondary text-label-sm-regular mb-3'>Filter Recipients</p>
                    <StanFilters
                        filters={filters}
                        filterValues={filterValues}
                        onFilterChanged={handleFilterChange}
                        onResetFilters={handleResetFilters}
                        placement='bottom-start'
                    />
                </div>

                <RecipientsTable />
            </StanCard>

            <ExportCsvModal
                isOpen={exportModal.isOpen}
                totalPages={totalExportPages}
                filename={`Stan_Recipients_${new Date().toISOString().split('T')[0]}.csv`}
                onClose={exportModal.close}
                onDataFetched={handleCSVDataFetched}
                fetchPageData={fetchPageData}
            />

            <SendOptInEmailModal isOpen={optInModal.isOpen} onClose={optInModal.close} onSuccess={handleOptInSuccess} />

            <SendOptInEmailSuccessModal
                isOpen={successModal.isOpen}
                emailsSent={emailsSent}
                onClose={handleSuccessModalClose}
            />
        </>
    );
};
